import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AgentChat } from './AgentChat'
import { usePreviewSession } from '../hooks/usePreviewSession'

interface BuildViewProps {
  projectId: string
  agentId: string | null
  projectName: string
  projectType?: string
  projectLanguage?: string
  showSignupCTA?: boolean
  showAdvancedLink?: boolean
}

const terminalTypes = ['terminal', 'library']

type Line = { prompt: boolean; text: string }

function getTerminalLines(name: string, language: string): Line[] {
  switch (language.toLowerCase()) {
    case 'go':
    case 'golang':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go mod tidy' },
        { prompt: false, text: 'go: downloading dependencies...' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go build -o ' + name + ' ./cmd/' + name },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ./${name} --help` },
        { prompt: false, text: `Usage: ${name} [options] <command>` },
        { prompt: false, text: '' },
        { prompt: false, text: 'Commands:' },
        { prompt: false, text: '  start       Start the application' },
        { prompt: false, text: '  config      Show configuration' },
        { prompt: false, text: '  version     Print version info' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Options:' },
        { prompt: false, text: '  -h, --help      Show this help message' },
        { prompt: false, text: '  -v, --verbose   Enable verbose output' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ./${name} start` },
        { prompt: false, text: `[INFO] ${name} v1.0.0 starting...` },
        { prompt: false, text: '[INFO] Configuration loaded' },
        { prompt: false, text: '[INFO] Ready.' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go test ./...' },
        { prompt: false, text: 'ok  \t' + name + '/cmd\t0.012s' },
        { prompt: false, text: 'ok  \t' + name + '/internal\t0.034s' },
        { prompt: false, text: '' },
        { prompt: false, text: 'PASS' },
      ]

    case 'python':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ python -m venv .venv && source .venv/bin/activate' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ pip install -e .' },
        { prompt: false, text: 'Installing collected packages: ' + name },
        { prompt: false, text: 'Successfully installed ' + name + '-0.1.0' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ${name} --help` },
        { prompt: false, text: `Usage: ${name} [OPTIONS] COMMAND [ARGS]...` },
        { prompt: false, text: '' },
        { prompt: false, text: 'Commands:' },
        { prompt: false, text: '  start       Start the application' },
        { prompt: false, text: '  config      Show configuration' },
        { prompt: false, text: '  version     Print version info' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Options:' },
        { prompt: false, text: '  --help      Show this message and exit' },
        { prompt: false, text: '  --verbose   Enable verbose output' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ${name} start` },
        { prompt: false, text: `[INFO] ${name} v0.1.0 starting...` },
        { prompt: false, text: '[INFO] Configuration loaded' },
        { prompt: false, text: '[INFO] Ready.' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ pytest' },
        { prompt: false, text: 'collected 4 items' },
        { prompt: false, text: '' },
        { prompt: false, text: 'tests/test_main.py ..          [50%]' },
        { prompt: false, text: 'tests/test_config.py ..        [100%]' },
        { prompt: false, text: '' },
        { prompt: false, text: '4 passed in 0.32s' },
      ]

    case 'rust':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ cargo build --release' },
        { prompt: false, text: '   Compiling ' + name + ' v0.1.0' },
        { prompt: false, text: '    Finished release [optimized] target(s) in 2.4s' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ./target/release/${name} --help` },
        { prompt: false, text: `Usage: ${name} [OPTIONS] <COMMAND>` },
        { prompt: false, text: '' },
        { prompt: false, text: 'Commands:' },
        { prompt: false, text: '  start       Start the application' },
        { prompt: false, text: '  config      Show configuration' },
        { prompt: false, text: '  version     Print version info' },
        { prompt: false, text: '  help        Print this message' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Options:' },
        { prompt: false, text: '  -h, --help      Print help' },
        { prompt: false, text: '  -v, --verbose   Enable verbose output' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ./target/release/${name} start` },
        { prompt: false, text: `[INFO] ${name} v0.1.0 starting...` },
        { prompt: false, text: '[INFO] Configuration loaded' },
        { prompt: false, text: '[INFO] Ready.' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ cargo test' },
        { prompt: false, text: '   Compiling ' + name + ' v0.1.0' },
        { prompt: false, text: '     Running unittests src/main.rs' },
        { prompt: false, text: '' },
        { prompt: false, text: 'running 3 tests' },
        { prompt: false, text: 'test tests::test_config ... ok' },
        { prompt: false, text: 'test tests::test_start ... ok' },
        { prompt: false, text: 'test tests::test_version ... ok' },
        { prompt: false, text: '' },
        { prompt: false, text: 'test result: ok. 3 passed; 0 failed' },
      ]

    case 'java':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ mvn package -q' },
        { prompt: false, text: '[INFO] Building ' + name + ' 1.0.0' },
        { prompt: false, text: '[INFO] BUILD SUCCESS' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ java -jar target/${name}-1.0.0.jar --help` },
        { prompt: false, text: `Usage: ${name} [options] <command>` },
        { prompt: false, text: '' },
        { prompt: false, text: 'Commands:' },
        { prompt: false, text: '  start       Start the application' },
        { prompt: false, text: '  config      Show configuration' },
        { prompt: false, text: '  version     Print version info' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Options:' },
        { prompt: false, text: '  -h, --help      Show this help message' },
        { prompt: false, text: '  -v, --verbose   Enable verbose output' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ java -jar target/${name}-1.0.0.jar start` },
        { prompt: false, text: `[INFO] ${name} v1.0.0 starting...` },
        { prompt: false, text: '[INFO] Configuration loaded' },
        { prompt: false, text: '[INFO] Ready.' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ mvn test -q' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Tests run: 4, Failures: 0, Errors: 0, Skipped: 0' },
        { prompt: false, text: '' },
        { prompt: false, text: '[INFO] BUILD SUCCESS' },
      ]

    default: // typescript
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm install' },
        { prompt: false, text: 'added 42 packages in 2.1s' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm run build' },
        { prompt: false, text: 'Compiling TypeScript...' },
        { prompt: false, text: 'Build complete.' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ./${name} --help` },
        { prompt: false, text: `Usage: ${name} [options] <command>` },
        { prompt: false, text: '' },
        { prompt: false, text: 'Commands:' },
        { prompt: false, text: '  start       Start the application' },
        { prompt: false, text: '  config      Show configuration' },
        { prompt: false, text: '  version     Print version info' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Options:' },
        { prompt: false, text: '  -h, --help      Show this help message' },
        { prompt: false, text: '  -v, --verbose   Enable verbose output' },
        { prompt: false, text: '  -q, --quiet     Suppress output' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ./${name} start` },
        { prompt: false, text: `[INFO] ${name} v1.0.0 starting...` },
        { prompt: false, text: '[INFO] Configuration loaded' },
        { prompt: false, text: '[INFO] Ready.' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm test' },
        { prompt: false, text: 'Running tests...' },
        { prompt: false, text: '' },
        { prompt: false, text: '  PASS  src/index.test.ts' },
        { prompt: false, text: '  PASS  src/config.test.ts' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Tests:  2 passed, 2 total' },
        { prompt: false, text: 'Time:   0.84s' },
      ]
  }
}

function getUILines(name: string, language: string): Line[] {
  switch (language.toLowerCase()) {
    case 'go':
    case 'golang':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go mod tidy' },
        { prompt: false, text: 'go: downloading dependencies...' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go run cmd/server/main.go' },
        { prompt: false, text: `[INFO] ${name} v1.0.0 starting...` },
        { prompt: false, text: '[INFO] Serving static files from ./static' },
        { prompt: false, text: '[INFO] Server listening on :8080' },
        { prompt: false, text: '' },
        { prompt: false, text: '  \u2192 http://localhost:8080' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go test ./...' },
        { prompt: false, text: 'ok  \t' + name + '/cmd/server\t0.021s' },
        { prompt: false, text: 'ok  \t' + name + '/internal/handlers\t0.045s' },
        { prompt: false, text: '' },
        { prompt: false, text: 'PASS' },
      ]
    case 'python':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ python -m venv .venv && source .venv/bin/activate' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ pip install -r requirements.txt' },
        { prompt: false, text: 'Successfully installed Flask-3.0.0 Jinja2-3.1.3 ...' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ flask run --debug' },
        { prompt: false, text: ' * Serving Flask app "app"' },
        { prompt: false, text: ' * Debug mode: on' },
        { prompt: false, text: ' * Running on http://127.0.0.1:5000' },
        { prompt: false, text: '' },
        { prompt: false, text: '  \u2192 http://localhost:5000' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ pytest' },
        { prompt: false, text: 'collected 6 items' },
        { prompt: false, text: '' },
        { prompt: false, text: 'tests/test_app.py ....         [67%]' },
        { prompt: false, text: 'tests/test_routes.py ..        [100%]' },
        { prompt: false, text: '' },
        { prompt: false, text: '6 passed in 0.48s' },
      ]
    case 'rust':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ cargo build --release' },
        { prompt: false, text: '   Compiling ' + name + ' v0.1.0' },
        { prompt: false, text: '    Finished release [optimized] target(s) in 14.2s' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ./target/release/${name}` },
        { prompt: false, text: `[INFO] ${name} v0.1.0 starting...` },
        { prompt: false, text: '[INFO] Serving static files from ./public' },
        { prompt: false, text: '[INFO] Listening on 0.0.0.0:3000' },
        { prompt: false, text: '' },
        { prompt: false, text: '  \u2192 http://localhost:3000' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ cargo test' },
        { prompt: false, text: '     Running unittests src/main.rs' },
        { prompt: false, text: '' },
        { prompt: false, text: 'running 4 tests' },
        { prompt: false, text: 'test tests::test_index ... ok' },
        { prompt: false, text: 'test tests::test_routes ... ok' },
        { prompt: false, text: 'test tests::test_static ... ok' },
        { prompt: false, text: 'test tests::test_404 ... ok' },
        { prompt: false, text: '' },
        { prompt: false, text: 'test result: ok. 4 passed; 0 failed' },
      ]
    case 'java':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ mvn package -q' },
        { prompt: false, text: '[INFO] Building ' + name + ' 1.0.0' },
        { prompt: false, text: '[INFO] BUILD SUCCESS' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ java -jar target/${name}-1.0.0.jar` },
        { prompt: false, text: '' },
        { prompt: false, text: '  .   ____          _' },
        { prompt: false, text: " /\\\\ / ___'_ __ _ _(_)" },
        { prompt: false, text: "( ( )\\___ | '_ | '_| |" },
        { prompt: false, text: ' \\\\/  ___)| |_)| | | |' },
        { prompt: false, text: "  '  |____| .__|_| |_|" },
        { prompt: false, text: ':: Spring Boot ::        (v3.2.0)' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Tomcat started on port 8080' },
        { prompt: false, text: 'Started Application in 2.4 seconds' },
        { prompt: false, text: '' },
        { prompt: false, text: '  \u2192 http://localhost:8080' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ mvn test -q' },
        { prompt: false, text: 'Tests run: 5, Failures: 0, Errors: 0, Skipped: 0' },
        { prompt: false, text: '' },
        { prompt: false, text: '[INFO] BUILD SUCCESS' },
      ]
    default: // typescript/react
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm install' },
        { prompt: false, text: 'added 1247 packages in 8.3s' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm run dev' },
        { prompt: false, text: '' },
        { prompt: false, text: '  VITE v5.4.0  ready in 312ms' },
        { prompt: false, text: '' },
        { prompt: false, text: '  \u279c  Local:   http://localhost:5173/' },
        { prompt: false, text: '  \u279c  Network: http://192.168.1.42:5173/' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm test' },
        { prompt: false, text: '' },
        { prompt: false, text: '  PASS  src/App.test.tsx' },
        { prompt: false, text: '  PASS  src/components/Header.test.tsx' },
        { prompt: false, text: '  PASS  src/hooks/useData.test.ts' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Tests:  3 passed, 3 total' },
        { prompt: false, text: 'Time:   1.2s' },
      ]
  }
}

function getAPILines(name: string, language: string): Line[] {
  switch (language.toLowerCase()) {
    case 'go':
    case 'golang':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go mod tidy' },
        { prompt: false, text: 'go: downloading dependencies...' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go run .' },
        { prompt: false, text: `[INFO] ${name} API v1.0.0` },
        { prompt: false, text: '[INFO] Listening on :8080' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ curl http://localhost:8080/api/health' },
        { prompt: false, text: '{"status":"ok","version":"1.0.0"}' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go test ./...' },
        { prompt: false, text: 'ok  \t' + name + '/internal/api\t0.034s' },
        { prompt: false, text: 'ok  \t' + name + '/internal/db\t0.018s' },
        { prompt: false, text: '' },
        { prompt: false, text: 'PASS' },
      ]
    case 'python':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ pip install -e .' },
        { prompt: false, text: 'Successfully installed ' + name + '-0.1.0' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ uvicorn app.main:app --reload' },
        { prompt: false, text: 'INFO:     Uvicorn running on http://127.0.0.1:8000' },
        { prompt: false, text: 'INFO:     Started reloader process' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ curl http://localhost:8000/health' },
        { prompt: false, text: '{"status":"ok","version":"0.1.0"}' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ pytest' },
        { prompt: false, text: 'collected 5 items' },
        { prompt: false, text: '' },
        { prompt: false, text: 'tests/test_api.py ...          [60%]' },
        { prompt: false, text: 'tests/test_models.py ..        [100%]' },
        { prompt: false, text: '' },
        { prompt: false, text: '5 passed in 0.41s' },
      ]
    case 'rust':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ cargo build --release' },
        { prompt: false, text: '   Compiling ' + name + ' v0.1.0' },
        { prompt: false, text: '    Finished release [optimized] target(s) in 12.1s' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ ./target/release/${name}` },
        { prompt: false, text: `[INFO] ${name} API v0.1.0` },
        { prompt: false, text: '[INFO] Listening on 0.0.0.0:3000' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ curl http://localhost:3000/health' },
        { prompt: false, text: '{"status":"ok","version":"0.1.0"}' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ cargo test' },
        { prompt: false, text: 'running 3 tests' },
        { prompt: false, text: 'test tests::test_health ... ok' },
        { prompt: false, text: 'test tests::test_routes ... ok' },
        { prompt: false, text: 'test tests::test_404 ... ok' },
        { prompt: false, text: '' },
        { prompt: false, text: 'test result: ok. 3 passed; 0 failed' },
      ]
    case 'java':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ mvn package -q' },
        { prompt: false, text: '[INFO] BUILD SUCCESS' },
        { prompt: false, text: '' },
        { prompt: true, text: `$ java -jar target/${name}-1.0.0.jar` },
        { prompt: false, text: 'Tomcat started on port 8080' },
        { prompt: false, text: 'Started Application in 2.4 seconds' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ curl http://localhost:8080/api/health' },
        { prompt: false, text: '{"status":"ok","version":"1.0.0"}' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ mvn test -q' },
        { prompt: false, text: 'Tests run: 4, Failures: 0, Errors: 0, Skipped: 0' },
        { prompt: false, text: '' },
        { prompt: false, text: '[INFO] BUILD SUCCESS' },
      ]
    default: // typescript/node
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm install' },
        { prompt: false, text: 'added 86 packages in 3.1s' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm start' },
        { prompt: false, text: `> ${name}@1.0.0 start` },
        { prompt: false, text: '> ts-node src/index.ts' },
        { prompt: false, text: '' },
        { prompt: false, text: '[INFO] Server listening on port 3000' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ curl http://localhost:3000/health' },
        { prompt: false, text: '{"status":"ok","version":"1.0.0"}' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm test' },
        { prompt: false, text: '  PASS  src/routes.test.ts' },
        { prompt: false, text: '  PASS  src/middleware.test.ts' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Tests:  2 passed, 2 total' },
        { prompt: false, text: 'Time:   0.92s' },
      ]
  }
}

function getLibraryLines(name: string, language: string): Line[] {
  switch (language.toLowerCase()) {
    case 'go':
    case 'golang':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go mod tidy' },
        { prompt: false, text: 'go: downloading dependencies...' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go build ./...' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go vet ./...' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ go test -v ./...' },
        { prompt: false, text: '=== RUN   TestNew' },
        { prompt: false, text: '--- PASS: TestNew (0.00s)' },
        { prompt: false, text: '=== RUN   TestParse' },
        { prompt: false, text: '--- PASS: TestParse (0.01s)' },
        { prompt: false, text: '=== RUN   TestValidate' },
        { prompt: false, text: '--- PASS: TestValidate (0.00s)' },
        { prompt: false, text: '' },
        { prompt: false, text: 'PASS' },
        { prompt: false, text: 'ok  \t' + name + '\t0.024s' },
      ]
    case 'python':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ pip install -e ".[dev]"' },
        { prompt: false, text: 'Successfully installed ' + name + '-0.1.0' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ pytest -v' },
        { prompt: false, text: 'collected 5 items' },
        { prompt: false, text: '' },
        { prompt: false, text: 'tests/test_core.py::test_create PASSED' },
        { prompt: false, text: 'tests/test_core.py::test_parse PASSED' },
        { prompt: false, text: 'tests/test_core.py::test_validate PASSED' },
        { prompt: false, text: 'tests/test_utils.py::test_format PASSED' },
        { prompt: false, text: 'tests/test_utils.py::test_convert PASSED' },
        { prompt: false, text: '' },
        { prompt: false, text: '5 passed in 0.29s' },
      ]
    case 'rust':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ cargo build' },
        { prompt: false, text: '   Compiling ' + name + ' v0.1.0' },
        { prompt: false, text: '    Finished dev [unoptimized + debuginfo] target(s)' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ cargo test' },
        { prompt: false, text: '     Running unittests src/lib.rs' },
        { prompt: false, text: '' },
        { prompt: false, text: 'running 4 tests' },
        { prompt: false, text: 'test tests::test_new ... ok' },
        { prompt: false, text: 'test tests::test_parse ... ok' },
        { prompt: false, text: 'test tests::test_validate ... ok' },
        { prompt: false, text: 'test tests::test_display ... ok' },
        { prompt: false, text: '' },
        { prompt: false, text: 'test result: ok. 4 passed; 0 failed' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ cargo doc --open' },
        { prompt: false, text: ' Documenting ' + name + ' v0.1.0' },
        { prompt: false, text: '    Finished dev target(s)' },
      ]
    case 'java':
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ mvn package' },
        { prompt: false, text: '[INFO] Building ' + name + ' 1.0.0' },
        { prompt: false, text: '[INFO] BUILD SUCCESS' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ mvn test' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Running com.example.CoreTest' },
        { prompt: false, text: 'Tests run: 3, Failures: 0, Errors: 0, Skipped: 0' },
        { prompt: false, text: 'Running com.example.UtilsTest' },
        { prompt: false, text: 'Tests run: 2, Failures: 0, Errors: 0, Skipped: 0' },
        { prompt: false, text: '' },
        { prompt: false, text: '[INFO] BUILD SUCCESS' },
      ]
    default: // typescript
      return [
        { prompt: true, text: `$ cd ${name}` },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm install' },
        { prompt: false, text: 'added 42 packages in 2.1s' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm run build' },
        { prompt: false, text: 'Compiling TypeScript...' },
        { prompt: false, text: 'Build complete.' },
        { prompt: false, text: '' },
        { prompt: true, text: '$ npm test' },
        { prompt: false, text: '' },
        { prompt: false, text: '  PASS  src/index.test.ts' },
        { prompt: false, text: '  PASS  src/utils.test.ts' },
        { prompt: false, text: '  PASS  src/parser.test.ts' },
        { prompt: false, text: '' },
        { prompt: false, text: 'Tests:  3 passed, 3 total' },
        { prompt: false, text: 'Time:   0.76s' },
      ]
  }
}

function getExampleLines(name: string, language: string, type: string): Line[] {
  const t = (type || 'terminal').toLowerCase()
  if (['ui', 'fullstack', 'both'].includes(t)) return getUILines(name, language)
  if (['api', 'service'].includes(t)) return getAPILines(name, language)
  if (t === 'library') return getLibraryLines(name, language)
  return getTerminalLines(name, language)
}

function TerminalPreview({ projectName, language, type }: { projectName: string; language: string; type: string }) {
  const name = projectName || 'my-project'
  const [visibleLines, setVisibleLines] = useState(0)

  const lines = getExampleLines(name, language, type)

  useEffect(() => {
    if (visibleLines >= lines.length) return
    const delay = lines[visibleLines]?.prompt ? 800 : 120
    const timer = setTimeout(() => setVisibleLines(v => v + 1), delay)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-lint baseline: deps intentionally omitted
  }, [visibleLines, lines.length])

  return (
    <div className="h-full bg-gray-950 text-green-400 font-mono text-sm p-4 overflow-y-auto">
      {lines.slice(0, visibleLines).map((line, i) => (
        <div key={i} className={`leading-6 ${line.prompt ? 'text-green-300' : 'text-gray-400'}`}>
          {line.text || '\u00A0'}
        </div>
      ))}
      {visibleLines < lines.length && (
        <span className="inline-block w-2 h-4 bg-green-400 animate-pulse" />
      )}
    </div>
  )
}

const phases = [
  { key: 'planning', label: 'Planning', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
  { key: 'coding', label: 'Coding', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' },
  { key: 'testing', label: 'Testing', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  { key: 'complete', label: 'Done', icon: 'M5 13l4 4L19 7' },
] as const

function PhaseIndicator({ currentPhase }: { currentPhase: string }) {
  const phaseIdx = phases.findIndex(p => p.key === currentPhase)

  return (
    <div className="flex items-center gap-1 w-full">
      {phases.map((phase, i) => {
        const isDone = i < phaseIdx
        const isActive = i === phaseIdx
        const isFuture = i > phaseIdx

        return (
          <div key={phase.key} className="flex items-center flex-1">
            <div className="flex items-center gap-1.5 flex-1">
              <div className={`
                w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500
                ${isDone ? 'bg-green-500 shadow-sm shadow-green-500/30' : ''}
                ${isActive ? 'bg-primary-500 shadow-sm shadow-primary-500/30 ring-2 ring-primary-500/20' : ''}
                ${isFuture ? 'bg-gray-200 dark:bg-gray-700' : ''}
              `}>
                {isDone ? (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className={`w-3 h-3 ${isActive ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={phase.icon} />
                  </svg>
                )}
              </div>
              <span className={`
                hidden sm:inline text-[10px] font-medium tracking-wide transition-colors duration-300
                ${isDone ? 'text-green-600 dark:text-green-400' : ''}
                ${isActive ? 'text-primary-600 dark:text-primary-400' : ''}
                ${isFuture ? 'text-gray-400 dark:text-gray-500' : ''}
              `}>
                {phase.label}
              </span>
            </div>
            {i < phases.length - 1 && (
              <div className={`
                h-px flex-1 mx-1 transition-colors duration-500
                ${i < phaseIdx ? 'bg-green-400 dark:bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}
              `} />
            )}
          </div>
        )
      })}
    </div>
  )
}

function StatsBar({ filesCreated, linesWritten, testsRun, isRunning }: { filesCreated: number; linesWritten: number; testsRun: number; isRunning: boolean }) {
  if (!isRunning && filesCreated === 0 && linesWritten === 0 && testsRun === 0) return null

  return (
    <div className="flex items-center gap-4 px-4 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
      {filesCreated > 0 && (
        <div className="flex items-center gap-1 animate-fadeIn">
          <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="tabular-nums font-medium">{filesCreated}</span> files
        </div>
      )}
      {linesWritten > 0 && (
        <div className="flex items-center gap-1 animate-fadeIn">
          <svg className="w-3 h-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
          <span className="tabular-nums font-medium">{linesWritten}</span> lines
        </div>
      )}
      {testsRun > 0 && (
        <div className="flex items-center gap-1 animate-fadeIn">
          <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="tabular-nums font-medium">{testsRun}</span> tests
        </div>
      )}
    </div>
  )
}

export function BuildView({ projectId, agentId, projectName, projectType, projectLanguage, showSignupCTA, showAdvancedLink }: BuildViewProps) {
  const isTerminal = projectType ? terminalTypes.includes(projectType) : false
  const navigate = useNavigate()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const previewSession = usePreviewSession(projectId, !isTerminal)
  const freshPreview = previewSession.fresh
  const [agentRunning, setAgentRunning] = useState(!!agentId)
  const [buildStats, setBuildStats] = useState<{ filesCreated: number; linesWritten: number; testsRun: number; phase: string }>({ filesCreated: 0, linesWritten: 0, testsRun: 0, phase: 'planning' })
  const refreshCountRef = useRef(0)

  const handleAgentStatusChange = useCallback((running: boolean) => {
    setAgentRunning(running)
    if (!running) {
      refreshCountRef.current = 0
    }
  }, [])

  // Auto-refresh preview iframe every 30s while agent is building,
  // then continue refreshing a few more times after agent completes
  useEffect(() => {
    if (isTerminal) return

    const interval = setInterval(() => {
      if (!agentRunning) {
        refreshCountRef.current++
        if (refreshCountRef.current > 4) {
          return
        }
      }

      try {
        iframeRef.current?.contentWindow?.location.reload()
      } catch {
        // Cross-origin preview: reload via a fresh signed link.
        void freshPreview().then((s) => {
          if (s && iframeRef.current) iframeRef.current.src = s.url
        })
      }
    }, 30000)

    return () => clearInterval(interval)
  }, [isTerminal, agentRunning, projectId, freshPreview])

  const handleSignup = () => {
    sessionStorage.setItem('pending_claim_project', projectId)
    navigate('/register')
  }

  return (
    <div className="flex flex-col h-full">
      {/* Enhanced project header */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-3">
            {/* Animated status indicator */}
            <div className="relative">
              <div className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${agentRunning ? 'bg-green-400' : 'bg-green-500'}`} />
              {agentRunning && (
                <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-green-400 animate-ping opacity-75" />
              )}
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{projectName || 'Your Project'}</span>
              <span className={`
                ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium
                ${agentRunning
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                  : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'}
              `}>
                {agentRunning && (
                  <span className="flex gap-0.5">
                    <span className="w-1 h-1 rounded-full bg-current animate-bounce-dot" />
                    <span className="w-1 h-1 rounded-full bg-current animate-bounce-dot" style={{ animationDelay: '0.16s' }} />
                    <span className="w-1 h-1 rounded-full bg-current animate-bounce-dot" style={{ animationDelay: '0.32s' }} />
                  </span>
                )}
                {agentRunning ? 'Building' : 'Complete'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showSignupCTA && (
              <button
                onClick={handleSignup}
                className="px-3 py-1.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white text-xs font-medium rounded-md hover:from-primary-500 hover:to-primary-400 transition-all shadow-sm shadow-primary-500/20"
              >
                Sign up to save
              </button>
            )}
            {showAdvancedLink && (
              <Link
                to={`/projects/${projectId}`}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Open full project
              </Link>
            )}
          </div>
        </div>

        {/* Phase progress stepper */}
        <div className="px-4 pb-2">
          <PhaseIndicator currentPhase={buildStats.phase} />
        </div>
      </div>

      {/* Side-by-side on desktop, stacked on mobile */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        {/* Left: Agent activity feed */}
        <div className="w-full md:w-1/2 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 flex flex-col bg-white dark:bg-gray-900 min-h-[40vh] md:min-h-0">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/80">
            <div className="flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Build Activity</span>
            </div>
            {agentRunning && (
              <span className="flex items-center gap-1.5 text-[10px] text-primary-500 font-medium">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
                </span>
                LIVE
              </span>
            )}
          </div>
          <StatsBar
            filesCreated={buildStats.filesCreated}
            linesWritten={buildStats.linesWritten}
            testsRun={buildStats.testsRun}
            isRunning={agentRunning}
          />
          <div className="flex-1 overflow-hidden">
            {agentId ? (
              <AgentChat
                agentId={agentId}
                projectId={projectId}
                isPublic={showSignupCTA}
                onAgentStatusChange={handleAgentStatusChange}
                onStatsChange={setBuildStats}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-3 animate-fadeIn">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full border-2 border-primary-200 dark:border-primary-800" />
                  <div className="absolute inset-0 w-10 h-10 rounded-full border-2 border-transparent border-t-primary-500 animate-spin" />
                </div>
                <p className="text-sm text-gray-400">Connecting to agent...</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: Live Preview or Terminal Example */}
        <div className="w-full md:w-1/2 flex flex-col bg-gray-50 dark:bg-gray-800 min-h-[40vh] md:min-h-0">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isTerminal ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              )}
            </svg>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {isTerminal ? 'Example Run' : 'Live Preview'}
            </span>
          </div>
          <div className="flex-1 relative">
            {isTerminal ? (
              <TerminalPreview projectName={projectName} language={projectLanguage || 'typescript'} type={projectType || 'terminal'} />
            ) : (
              previewSession.url && <iframe
                ref={iframeRef}
                src={previewSession.url}
                className="w-full h-full border-0 bg-white dark:bg-gray-900"
                title="Project Preview"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom banner (public CTA) */}
      {showSignupCTA && (
        <div className="flex items-center justify-between px-6 py-3 bg-gradient-to-r from-gray-50 to-primary-50/30 dark:from-gray-800 dark:to-primary-900/10 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-orange-400 flex items-center justify-center flex-shrink-0 shadow-sm">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {agentRunning ? 'Your project is being built live.' : 'Your project is ready!'}{' '}
              <span className="font-medium text-gray-900 dark:text-gray-200">Sign up to save it and keep building.</span>
            </p>
          </div>
          <button
            onClick={handleSignup}
            className="px-4 py-2 bg-gradient-to-r from-primary-600 to-orange-500 text-white text-sm font-semibold rounded-lg hover:from-primary-500 hover:to-orange-400 transition-all shadow-md shadow-primary-500/20 hover:shadow-lg hover:shadow-primary-500/30 hover:-translate-y-px flex-shrink-0"
          >
            Sign up free
          </button>
        </div>
      )}
    </div>
  )
}
